import React, { useCallback, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import './PayrollWorkspace.css';

interface PayCollapsibleProps {
  title: string;
  /** Optional supporting line, shown under the title in the header row. */
  description?: string;
  /** Right-aligned summary of what is inside, so the panel stays useful shut. */
  meta?: React.ReactNode;
  defaultOpen?: boolean;
  /** Persists the open state across visits when set. */
  storageKey?: string;
  /** Anchors the payroll product tour; kept on the outer section. */
  tourTarget?: string;
  children: React.ReactNode;
}

function readStoredOpen(storageKey: string | undefined, fallback: boolean): boolean {
  if (!storageKey) return fallback;
  try {
    const stored = window.localStorage.getItem(storageKey);
    return stored === null ? fallback : stored === 'true';
  } catch {
    return fallback;
  }
}

/**
 * A payroll panel that stays shut until asked for. Reference tables — the
 * period archive, the aggregate report, the rule editor — are read rarely but
 * used to occupy the screen ahead of the review table, so they live in here.
 */
export default function PayCollapsible({
  title,
  description,
  meta,
  defaultOpen = false,
  storageKey,
  tourTarget,
  children,
}: PayCollapsibleProps) {
  const [isOpen, setIsOpen] = useState(() => readStoredOpen(storageKey, defaultOpen));

  const toggle = useCallback(() => {
    setIsOpen((open) => {
      if (storageKey) {
        try {
          window.localStorage.setItem(storageKey, String(!open));
        } catch {
          // Preference only.
        }
      }
      return !open;
    });
  }, [storageKey]);

  return (
    <section
      className={`pay-panel pay-panel--collapsible ${isOpen ? 'pay-panel--open' : ''}`}
      data-tour={tourTarget}
    >
      <button type="button" className="pay-panel__toggle" onClick={toggle} aria-expanded={isOpen}>
        <span className="pay-panel__toggle-copy">
          <h2>{title}</h2>
          {description && <p>{description}</p>}
        </span>
        {meta && <span className="pay-panel__toggle-meta">{meta}</span>}
        <FiChevronDown className="pay-panel__chevron" aria-hidden="true" />
      </button>
      {isOpen && <div className="pay-panel__body">{children}</div>}
    </section>
  );
}
